// Shared modals & primitives for UsersAdmin
import { useState } from "react";
import { AlertCircle, X } from "lucide-react";
import { USERS } from "@/constants/testIds";
import { PasswordInput, PasswordStrength, isStrongPassword, PASSWORD_POLICY_MSG } from "@/components/PasswordInput";

export const inputCls =
  "w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded focus:border-[#c8a04a] focus:outline-none";

export function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        data-testid={testid}
        className="w-full max-w-lg rounded border border-[#d9cfb8] bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-[#1c1a14]">{title}</h3>
          <button onClick={onClose} className="text-[#8b7e5e] hover:text-[#1c1a14]">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

export function UserFormModal({ onClose, onSubmit }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("secretaria");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.includes("@")) {
      setError("Correo electrónico inválido");
      return;
    }
    if (!isStrongPassword(password)) {
      setError(PASSWORD_POLICY_MSG);
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ email: email.trim().toLowerCase(), password, role });
    } catch (msg) {
      setError(typeof msg === "string" ? msg : "Error al crear usuario");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal testid={USERS.formModal} title="Nuevo usuario" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Correo electrónico *">
          <input
            data-testid={USERS.emailInput}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            autoFocus
          />
        </Field>
        <Field label="Contraseña * (mín. 8 · 1 mayúscula · 1 número)">
          <PasswordInput
            testid={USERS.passwordInput}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
          />
          <PasswordStrength password={password} />
        </Field>
        <Field label="Rol *">
          <select
            data-testid={USERS.roleSelect}
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className={inputCls}
          >
            <option value="secretaria">Secretaría (Células + Consolidación)</option>
            <option value="administrador">Administrador (Acceso total)</option>
          </select>
        </Field>

        {error && (
          <div
            data-testid={USERS.errorMsg}
            className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700"
          >
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid={USERS.cancelButton}
            onClick={onClose}
            disabled={busy}
            className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid={USERS.saveButton}
            disabled={busy}
            className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-50"
          >
            {busy ? "Guardando..." : "Crear"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ResetPasswordModal({ user, onClose, onSubmit }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isStrongPassword(password)) {
      setError(PASSWORD_POLICY_MSG);
      return;
    }
    setBusy(true);
    try {
      await onSubmit(password);
    } catch (msg) {
      setError(typeof msg === "string" ? msg : "Error al actualizar contraseña");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Restablecer contraseña — ${user.email}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Nueva contraseña * (mín. 8 · 1 mayúscula · 1 número)">
          <PasswordInput
            testid={USERS.resetPasswordInput}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
            autoFocus
          />
          <PasswordStrength password={password} />
        </Field>

        {error && (
          <div className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid={USERS.resetPasswordSubmit}
            disabled={busy}
            className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-50"
          >
            {busy ? "Guardando..." : "Actualizar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ChangeEmailModal({ user, isSelf, onClose, onSubmit }) {
  const [email, setEmail] = useState(user.email);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const next = email.trim().toLowerCase();
    if (!next.includes("@") || next.length < 5) {
      setError("Correo electrónico inválido");
      return;
    }
    if (next === user.email.toLowerCase()) {
      setError("El correo es igual al actual");
      return;
    }
    setBusy(true);
    try {
      await onSubmit(next);
    } catch (msg) {
      setError(typeof msg === "string" ? msg : "Error al actualizar correo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Cambiar correo — ${user.email}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Nuevo correo electrónico *">
          <input
            data-testid={USERS.changeEmailInput}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            autoFocus
          />
        </Field>

        {isSelf && (
          <div className="flex items-start gap-2 rounded border border-[#d4a017] bg-[#fdf6e3] px-3 py-2 text-[12px] text-[#7a5b0f]">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>
              Estás cambiando <strong>tu propio correo</strong>: al guardar se
              cerrará tu sesión y deberás entrar de nuevo con el correo nuevo
              (tu contraseña no cambia).
            </span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid={USERS.changeEmailSubmit}
            disabled={busy}
            className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-50"
          >
            {busy ? "Guardando..." : "Actualizar correo"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ConfirmModal({ title, message, onCancel, onConfirm, confirmLabel = "Eliminar" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded border border-[#d9cfb8] bg-white p-6 shadow-xl">
        <div className="text-base font-medium text-[#1c1a14]">{title}</div>
        <div className="mt-2 text-[13px] text-[#5b5240]">{message}</div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="rounded border border-red-700 bg-red-700 px-3 py-1.5 text-[12px] uppercase tracking-widest text-white hover:bg-red-800"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
